import { JsonPipe } from '@angular/common';
import { Component, computed, effect, input, signal } from '@angular/core';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';

import { FormlyFieldConfig, FormlyForm } from '@ngx-formly/core';
import {
  AutoSaveFormBase,
  FormGroupControlsOf,
} from '../base-classes/base-form.class';
import {
  ISurveyPlugin,
  SurveyPluginUsage,
} from '@hyper-inferno/survey-elements-core';

@Component({
  selector: 'app-plugin-usage-detail',
  imports: [ReactiveFormsModule, FormlyForm, JsonPipe],
  templateUrl: './plugin-usage-detail.html',
  styleUrl: './plugin-usage-detail.css',
})
export class PluginUsageDetail extends AutoSaveFormBase<SurveyPluginUsage> {
  override entityName = 'survey_plugin_usage';
  entityId = input.required<string>(); //Plugin usage id

  override form = this.fb.group<FormGroupControlsOf<SurveyPluginUsage>>({
    id: this.fb.control<any | undefined>(undefined),
    plugin_id: this.fb.control<any | undefined>(undefined),
    element_id: this.fb.control<any | undefined>(undefined),
    page_id: this.fb.control<any | undefined>(undefined),
    survey_id: this.fb.control<any | undefined>(undefined),
    plugin_values: this.fb.group({}) as any, // Initialize as an empty group, formly will populate it // instance values
    plugin_type: this.fb.control<any | undefined>(undefined),
  });

  get pluginValuesGroup(): FormGroup {
    return this.form.get('plugin_values') as FormGroup;
  }

  // The model passed to Formly should only be the nested values
  model = {
    plugin_values: {} as any,
  };

  currentIdPluginId = signal<string | undefined>(undefined);
  surveyPlugins = input.required<ISurveyPlugin[]>();
  isSchemaReady = signal<boolean>(false);
  dynamicFields = computed<FormlyFieldConfig[]>(() => {
    if (!this.currentIdPluginId() || this.surveyPlugins().length === 0)
      return [];

    const matchedPlugin = this.surveyPlugins().find(
      (p) => p.id === this.currentIdPluginId(),
    );
    if (!matchedPlugin || !matchedPlugin.config_schema) return [];
    try {
      return typeof matchedPlugin.config_schema === 'string'
        ? JSON.parse(matchedPlugin.config_schema)
        : (matchedPlugin.config_schema as FormlyFieldConfig[]);
    } catch (e) {
      console.error('Failed to parse dynamic config_schema payload:', e);
      return [];
    }
  });

  constructor() {
    super();
    effect(() => {
      const fieldsExist = this.dynamicFields().length > 0;
      const hasValues = !!this.model.plugin_values;

      if (fieldsExist && hasValues) {
        this.isSchemaReady.set(true);
      } else {
        this.isSchemaReady.set(false);
      }
    });
    effect(() => {
      const uuid = this.entityId();
      if (uuid) {
        this.form.patchValue({ id: uuid }, { emitEvent: false });
      }
    });
    // 3. Connect the legacy Angular Form element to your Signal
    this.form.get('plugin_id')?.valueChanges.subscribe((value) => {
      this.currentIdPluginId.set(value ?? undefined);
    });
  }

  protected override rebuildFormArrays(data: any): void {
    if (!data) {
      // console.warn(
      //   'rebuildFormArrays exited early: "data" is null or undefined.',
      // );
      return;
    }

    // 1. Safe Patch for main ID
    if (data.id) {
      if (this.form) {
        this.form.patchValue({ id: data.id }, { emitEvent: false });
      } else {
      }
    }

    // 2. Safely parse and handle plugin values
    let parsedValues: Record<string, any> = {};
    // console.log('Type of data.plugin_values:', typeof data.plugin_values);

    if (data.plugin_values) {
      if (typeof data.plugin_values === 'string') {
        try {
          parsedValues = JSON.parse(data.plugin_values);
          // console.log(
          //   'Successfully parsed plugin_values string:',
          //   parsedValues,
          // );
        } catch (error) {
          console.error('Failed to parse plugin_values JSON string:', error);
          parsedValues = {}; // Fallback to prevent crash
        }
      } else {
        parsedValues = { ...data.plugin_values };
      }
    } else {
    }

    // 3. Update model without completely overwriting other model properties
    this.model = {
      ...this.model,
      plugin_values: parsedValues,
    };
    // 4. Patch the subgroup if it exists
    if (this.pluginValuesGroup) {
      // console.log('Patching pluginValuesGroup with:', parsedValues);
      try {
        this.pluginValuesGroup.patchValue(parsedValues, { emitEvent: false });
      } catch (formError) {
        console.error('Error while patching pluginValuesGroup:', formError);
      }
    } else {
    }

    // 5. Update signals / identifiers
    if (data.plugin_id) {
      // console.log('Setting currentIdPluginId signal to:', data.plugin_id);
      if (
        this.currentIdPluginId &&
        typeof this.currentIdPluginId.set === 'function'
      ) {
        this.currentIdPluginId.set(data.plugin_id);
      } else {
        console.error(
          'CRITICAL: "this.currentIdPluginId" is not a valid Angular Signal.',
        );
      }
    }
  }

  // Becauase there is a nested From Group involved in this
  override getFormValidationErrors(): string[] {
    const messages: string[] = [];

    // Define a recursive helper function
    const extractErrors = (control: any, path: string = '') => {
      // 1. If it's a FormGroup, recurse into its child controls
      if (control instanceof FormGroup) {
        Object.keys(control.controls).forEach((key) => {
          const currentPath = path ? `${path} -> ${key}` : key;
          extractErrors(control.get(key), currentPath);
        });
      }
      // 2. If it's a FormArray, loop through its elements recursively
      else if (control instanceof FormArray) {
        control.controls.forEach((element, index) => {
          const currentPath = `${path}[${index}]`;
          extractErrors(element, currentPath);
        });
      }
      // 3. If it's a base FormControl, grab its errors
      else {
        const controlErrors = control?.errors;
        if (controlErrors) {
          Object.keys(controlErrors).forEach((keyError) => {
            messages.push(`Field "${path}" is invalid: ${keyError}`);
          });
        }
      }
    };

    // Kick off the recursion using the root form
    extractErrors(this.form);

    return messages;
  }
}
